import DOMPurify from 'dompurify';

/**
 * configuration for strict mode (no HTML tags allowed)
 */
const strictConfig = {
  ALLOWED_TAGS: [] as string[],
  ALLOWED_ATTR: [] as string[],
  KEEP_CONTENT: true,
};

/**
 * configuration for basic formatting
 */
const basicFormattingConfig = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * 
 * @param dirty - The HTML string to sanitize
 * @param allowBasicFormatting - Whether to allow basic HTML formatting tags (default: false)
 * @returns Sanitized HTML string safe for rendering
 * 
 * @example
 * // Strict mode (default) - strips all HTML
 * sanitizeHtml('<script>alert("XSS")</script>Hello') // Returns: "Hello"
 * sanitizeHtml('<p>Hello <b>World</b></p>') // Returns: "Hello World"
 * 
 * @example
 * // Basic formatting mode - allows safe tags
 * sanitizeHtml('<p>Hello <b>World</b></p>', true) // Returns: "<p>Hello <b>World</b></p>"
 * sanitizeHtml('<script>alert("XSS")</script>Hello', true) // Returns: "Hello"
 */
export const sanitizeHtml = (
  dirty: string | null | undefined,
  allowBasicFormatting: boolean = false
): string => {
  if (!dirty) {
    return '';
  }

  const config = allowBasicFormatting ? basicFormattingConfig : strictConfig;
  return DOMPurify.sanitize(dirty, config);
};

/**
 * Sanitizes an object recursively, sanitizing all string values
 * 
 * @param obj - The object to sanitize
 * @param allowBasicFormatting - Whether to allow basic HTML formatting tags
 * @returns Sanitized object
 * 
 * @example
 * const user = {
 *   name: '<script>alert("XSS")</script>John',
 *   bio: '<p>Hello <b>World</b></p>'
 * };
 * sanitizeObject(user) // Returns: { name: 'John', bio: 'Hello World' }
 */
export const sanitizeObject = <T>(
  obj: T,
  allowBasicFormatting: boolean = false
): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return sanitizeHtml(obj, allowBasicFormatting) as any;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, allowBasicFormatting)) as any;
  }

  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitized[key] = sanitizeObject((obj as any)[key], allowBasicFormatting);
      }
    }
    return sanitized;
  }

  return obj;
};

/**
 * Creates sanitized props for dangerouslySetInnerHTML
 * Use this when you need to render user-generated HTML content safely
 * 
 * @param html - The HTML string to sanitize
 * @param allowBasicFormatting - Whether to allow basic HTML formatting tags
 * @returns Object with __html property containing sanitized HTML
 * 
 * @example
 * // In a React component
 * <div dangerouslySetInnerHTML={createSafeHtml(userContent)} />
 */
export const createSafeHtml = (
  html: string | null | undefined,
  allowBasicFormatting: boolean = false
): { __html: string } => ({
  __html: sanitizeHtml(html, allowBasicFormatting),
});

